import { observable, autorun, runInAction, action, makeObservable } from 'mobx';
import { IonicAuth, AuthResult } from '@ionic-enterprise/auth';
import { isPlatform } from '@ionic/react';
import { nativeAzureConfig, webAzureConfig } from './environment';
import { VaultService } from './vault.service';
import { User, IDToken } from '../schemas/user';

import {
  KEYS,
  Roles,
  NO_AUTH_MSG,
  App_permission_message,
  App_permission_title,
  PREFERENCE_TABS,
  TAB_NAME,
} from '../utils/constants';
import {
  getDashboard,
  IONICWrapper,
  clear,
  getReduxData
} from '../utils/common';
import Environment from '../environments/environment';
import { store } from '../store';
import { setSessionInfo, setTabName } from '../actions/App/action';
import { AuthMode } from '@ionic-enterprise/identity-vault';
import { ToastMessage } from '../utils/ToastMessage';
import { IPreference } from '../schemas/App/App.Schema';
import { showPermissionAlert } from '../actions/Login/action';
import { getApplicationInfo } from '../utils/Analytics';
import { PREFERENCE_TAB, getRole, getShipmentByStateCall, getUserDetails, getUserInfo } from '../utils/auth-utils';

export class AuthenticationService extends IonicAuth<IDToken> {
  private vaultService: VaultService;

  /** Access token exists and the vault is unlocked. */
  @observable public isLoggedIn: boolean;
  @observable public user?: User;
  @observable public accessToken?: string;
  public isActive: boolean;
  onVaultLockChange: () => void;
  constructor(vaultService: VaultService) {
    
    const config = isPlatform('capacitor') ? nativeAzureConfig : webAzureConfig;
    try {
      vaultService.setAuthMode(AuthMode.SecureStorage);
    } catch (error) {
      throw new Error(error);
    }
    config.tokenStorageProvider = vaultService;
    super(config);
    this.vaultService = vaultService;
    this.isLoggedIn = false;
    store.dispatch(setTabName({"tab_name":"login"}));
    this.isActive = false;
    makeObservable(this);
    if (
      (isPlatform('mobileweb') || isPlatform('desktop')) &&
      !(isPlatform('hybrid') || isPlatform('capacitor'))
    ) {
      const authUrl = window.location.href;
      if (window.location.hash) {
        (async () => await this.handleCallback(authUrl))();
        /AADB2C90091/.test(authUrl) && this.login();
        if (/AADB2C90118/.test(authUrl)) {
          super.login(KEYS.AZURE_RESET_URL).catch((err: any) => {
            if (
              err.message !== undefined &&
              err.message.startsWith('AADB2C90091')
            ) {
              super.login();
            }
          });
        }
      }
    }
    this.onVaultLockChange = autorun(() => {
      this.setLoginState(this.vaultService.isLocked);
    });
  }

  async login(): Promise<void> {
    await this.vaultService.logout();

    try {
      await super.login(KEYS.DISCOVERY_URL);
    } catch (err) {
      // This is to handle the password reset case for Azure AD
      //  This only applicable to Azure AD.

      const message: string = err.message;

      if (
        message !== undefined &&
        !message.startsWith('AADB2C90118') &&
        !message.includes('callback')
      ) {
        await ToastMessage.show(NO_AUTH_MSG);
      }
      console.error('login error:', +err);

      // This is the error code returned by the Azure AD servers on failure.
      if (message !== undefined && message.startsWith('AADB2C90118')) {
        // The address you pass back is the custom user flow (policy) endpoint
        super.login(KEYS.AZURE_RESET_URL).catch((err: any) => {
          if (
            err.message !== undefined &&
            err.message.startsWith('AADB2C90091')
          ) {
            this.login();
          }
        });
      }
    }
  }

  async changePassword(): Promise<void> {
    try {
      await super.login(KEYS.AZURE_RESET_URL).catch((err: any) => {});
    } catch (err) {}
  }

  

  /**
   * method to check if session is active or not. if not user will be redirected to landing screen.
   * @param {boolean} tabSession flag for avoiding rerendering.
   * @param {any} setSession to invalidate the flag.
   */
  async hasSession(tabSession: boolean, setSession: any) {
    if (tabSession) {
      setSession(false);
      if (!this.isActive) {
        const isSessionActive = await this.isAuthenticated();

        if (!isSessionActive) {
          this.isLoggedIn = false;
        }
      }
      this.isActive = false;
    }
  }

  async isAuthenticated(): Promise<boolean> {
    const isVaultLocked = await this.vaultService.isLocked;
    return !isVaultLocked && (await super.isAuthenticated());
  }

  async setLoginState(isLocked: boolean, authResult: AuthResult = {}) {
    if (isLocked) {
      this.isLoggedIn = false;
      return;
    }

    this.isActive = true;

    const { accessToken } = authResult;

    // swallow vault errors
    try {
      const [hasSession, idTokenInfo, accessTokenString] = (await Promise.all([
        this.vaultService.hasStoredSession(),
        this.getIdToken(),
        accessToken ? Promise.resolve(accessToken) : this.getAccessToken(),
      ])) as [boolean, IDToken, string]; // https://github.com/microsoft/TypeScript/issues/33752;

      await new Environment(
        ((process.env.REACT_APP_ENV && process.env.REACT_APP_ENV.trim()) ||
          'dev') as any
      ).getConfig(
        IONICWrapper.details,
        this.vaultService,
        KEYS.SUBSCRIPTION_KEY,
        (await this.getAuthResponse()).id_token,
        KEYS.API_HOST
      );
      const tabName = getReduxData(TAB_NAME);
      if (tabName === 'login') {
        var manage: any = await this.getIdToken();
        const tabName = await getDashboard(
          manage.roles ? getRole(manage.roles) : Roles.carrierGuest,
          manage.roles
        );
        store.dispatch(setTabName({"tab_name":tabName}));
        IONICWrapper.details.getVault(await this.vaultService.getVault());

        IONICWrapper.accessToken = accessTokenString;
        IONICWrapper.details.getAuth({
          tokens: await this.getAuthResponse(),
          user: await this.getIdToken(),
        });

        // start -  Preference API  Call Invoked  for Desktop or mobile web.
        if ((isPlatform('desktop') || isPlatform('mobileweb')) && !(IONICWrapper.details.auth.user.roles.length === 1 &&
        IONICWrapper.details.auth.user.roles[0] === "carrierGuest")) {
          const preferences: any = await getUserDetails();
          const preference =
            preferences &&
            preferences.find(
              (pref: IPreference) => pref.key === 'viewsettings'
            );

          //Get active Tab name.
          let tabName = getReduxData(TAB_NAME);
          tabName =
            preference && preference.value != ''
              ? window.localStorage.getItem('REFRESH_URL')
                ? tabName
                : PREFERENCE_TAB[preference.value]
              : tabName;
          store.dispatch(setTabName({"tab_name":tabName}));
          if (preference.value === PREFERENCE_TABS.search ||
            preference.value === PREFERENCE_TABS.manageMyCapacity ||
            preference.value === PREFERENCE_TABS.manageUsers) {
              getShipmentByStateCall();
          }
              // batch state updates
              this.updateBatchStates(hasSession, idTokenInfo, accessTokenString);          
          }else{
          this.updateBatchStates(hasSession, idTokenInfo, accessTokenString);
        }
        // end -  Preference API  Call Invoked  for Desktop or mobile web.
      }

    } catch (err) {}
  }

  private updateBatchStates(hasSession: boolean, idTokenInfo: IDToken, accessTokenString: string){
    // batch state updates
    runInAction(() => {
      this.vaultService.hasSessionStored = hasSession;
      this.user = getUserInfo(idTokenInfo);
      this.accessToken = accessTokenString;
      this.isLoggedIn = true;
      getApplicationInfo().then(session => {
        store.dispatch(setSessionInfo({session}))
      })
    });
    getUserDetails();
  }

  async onLoginSuccess(authResult: AuthResult) {
    window.localStorage.removeItem('REFRESH_URL');

    // GPS Disclosure Alert for Mobile and Only for Android.
    if (
      isPlatform('android') &&
      !isPlatform('mobileweb') &&
      !isPlatform('desktop') &&
      window['localStorage']
    ) {
      if (!!!localStorage.getItem('App_Permission')) {
        store.dispatch(
          showPermissionAlert({
            isShowPermissionAlert: true,
            permissionAlertMessage: App_permission_message,
            permissionAlertTitle: App_permission_title,
          })
        );
        localStorage.setItem('App_Permission', 'true');
      }
    }
    // we pass isLocked as false, since we have access to all tokens in memory
    this.setLoginState(false, authResult);
  }

  @action
  async onLogout() {
    this.isLoggedIn = false;
    this.user = undefined;
    this.accessToken = undefined;
    this.vaultService.logout();
    this.vaultService.hasSessionStored = false;
    await clear();
    store.dispatch(setTabName({"tab_name":'login'}));
  }
}
